import { useCallback, useEffect, useRef } from 'react';

import { CONTAINER_ID } from '~/components/shared/recommendations/stylitics/constants';
import type {
  StyliticsConfiguration,
  StyliticsComposerInstance,
} from '~/components/shared/recommendations/stylitics/types';
import logger from '~/utilities/logger';

const STYLITICS_SCRIPT_ID = 'stylitics-script';

// Global variable - survives component re-renders but not page reloads
let isUnmounting = false;

export const useStyliticsComposer = (
  productCode: string,
  configuration: StyliticsConfiguration
) => {
  const composerRef = useRef<StyliticsComposerInstance | null>(null);
  const isInitialized = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const initializeStylitics = useCallback(async () => {
    if (!window?.StyliticsComposer || !productCode || isInitialized.current) {
      return;
    }

    if (isUnmounting) {
      timeoutRef.current = setTimeout(() => {
        initializeStylitics();
      }, 100);

      return;
    }

    try {
      // Initialize composer with configuration
      composerRef.current = await window?.StyliticsComposer.initialize(configuration);

      await composerRef.current.start();
      isInitialized.current = true;
    } catch (error) {
      logger.error(error);
    }
  }, [configuration, productCode]);

  const loadStyliticsScript = useCallback(() => {
    const existingScript = document.getElementById(STYLITICS_SCRIPT_ID) as HTMLScriptElement;

    if (existingScript) {
      // Check if script is already loaded
      if (window?.StyliticsComposer) {
        initializeStylitics();
      } else {
        // Script exists but not loaded yet, attach listener
        existingScript.addEventListener(
          'load',
          () => {
            initializeStylitics();
          },
          { once: true }
        );
      }

      return;
    }

    const script = document.createElement('script');

    script.src = 'https://web-assets.stylitics.com/loader/latest/composer.js';
    script.async = true;
    script.id = STYLITICS_SCRIPT_ID;

    script.onload = () => {
      initializeStylitics();
    };

    document.body.appendChild(script);
  }, [initializeStylitics]);

  useEffect(() => {
    if (productCode) {
      loadStyliticsScript();
    }
  }, [loadStyliticsScript, productCode]);

  useEffect(
    () => () => {
      isUnmounting = true;

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      composerRef.current
        ?.destroy()
        .catch(error => {
          const container = document.getElementById(CONTAINER_ID);

          if (container) {
            container.innerHTML = '';
          }

          logger.error('Failed to destroy Stylitics instance:', error);
        })
        .finally(() => {
          isInitialized.current = false;
          isUnmounting = false;
          composerRef.current = null;
        });
    },
    []
  );

  return composerRef?.current;
};
